// Copyright 2023, mochabug AB
//
// Licensed under the Apache License, Version 2.0 (the "License");
// you may not use this file except in compliance with the License.
// You may obtain a copy of the License at
//
//     http://www.apache.org/licenses/LICENSE-2.0
//
// Unless required by applicable law or agreed to in writing, software
// distributed under the License is distributed on an "AS IS" BASIS,
// WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
// See the License for the specific language governing permissions and
// limitations under the License.

import { checkbox, input, select } from '@inquirer/prompts';
import ora from 'ora';
import { Manifest } from './genproto/mochabugapis/adapt/plugins/v1/manifest_pb';
import { CapabilityJson } from './genproto/mochabugapis/adapt/plugins/v1/vertex_pb';
import { addVertex } from './vertices';

export async function add(manifest: Manifest) {
  const vertexName = await input({
    message: 'Type a new unused vertex name',
    transformer: (val: string): string => {
      return val.trim();
    },
    validate: (val: string): boolean => {
      val = val.trim();
      if (manifest.vertices.filter((item) => item.name === val).length > 0) {
        return false;
      }
      return /^[_a-z][_a-z0-9]*$/.test(val);
    }
  });
  const folder = await input({
    message: 'Where do you want to place the code?',
    default: `src/${vertexName}`,
    transformer: (val) => val.trim()
  });

  const capabilities: CapabilityJson[] = await checkbox({
    message: 'Choose the capabilities of your vertex',
    choices: [
      {
        name: 'Configurator',
        value: 'CAPABILITY_CONFIGURATOR',
        description:
          'Whether or not you want to have configurator attached to your vertex'
      },
      {
        name: 'Browser',
        value: 'CAPABILITY_BROWSER',
        description: 'Whether or not your vertex is a browser component'
      },
      {
        name: 'Cron',
        value: 'CAPABILITY_CRON',
        description:
          'A cron capability is a vertex that triggers at a specific time, such as every day at 3:00 PM.'
      },
      {
        name: 'External',
        value: 'CAPABILITY_EXTERNAL',
        description:
          'Whether or not your vertex requires an external endpoint. This has no effect with browser since a browser is already external but with additional restrictions'
      }
    ]
  });

  // Check which frontend to use, if any
  let frontendLib: 'none' | 'react' | undefined;
  if (
    capabilities.includes('CAPABILITY_CONFIGURATOR') ||
    capabilities.includes('CAPABILITY_BROWSER')
  ) {
    frontendLib = await select({
      message: 'Choose frontend library',
      choices: [
        {
          name: 'React',
          value: 'react',
          description: 'React with TypeScript'
        },
        {
          name: 'None',
          value: 'none',
          description: 'I do it myself'
        }
      ]
    });
  }

  const bootstrapSpinner = ora('Setting up the vertex...').start();
  await addVertex(process.cwd(), vertexName, folder, capabilities, frontendLib);
  bootstrapSpinner.succeed('Vertex has been added');
}
